import { DialogContent, DialogOverlay } from '@reach/dialog';
import React, { Suspense } from 'react';

import { tailwindMerge } from '../../../tailwind';
import { SpinnerAnimatedSolidIcon } from '../../Icons';
import type { ModalSize } from './ModalSize';

export const ModalOverlay = ({
  className,
  position,
  children,
  ...props
}: React.ComponentProps<typeof DialogOverlay> & {
  position: 'top' | 'center';
}) => (
  <DialogOverlay
    className={tailwindMerge(
      'bottom-zero left-zero right-zero top-zero z-modal bg-modal-overlay-color p-l backdrop-blur-s fixed flex justify-center',
      position === 'top' ? 'items-start' : 'items-center',
      className,
    )}
    {...props}
  >
    {children}
  </DialogOverlay>
);

const modalSizeToClass: Record<ModalSize, string> = {
  small: 'tablet:basis-modal-small',
  medium: 'tablet:basis-modal-medium',
  large: 'tablet:basis-modal-large',
};

interface ModalContentProps extends React.ComponentProps<typeof DialogContent> {
  size: ModalSize | 'fullWidth';
  fullHeight: boolean;
}

export const ModalContent = React.forwardRef<HTMLDivElement, ModalContentProps>(
  (
    { className, size, fullHeight, children, ...props }: ModalContentProps,
    ref,
  ) => (
    <DialogContent
      className={tailwindMerge(
        'bottom-zero left-zero right-zero top-zero m-zero bg-background-white p-zero tablet:static tablet:max-h-full tablet:overflow-visible tablet:rounded tablet:shadow-modal fixed flex w-full flex-col justify-between focus-visible:outline-hidden',
        size !== 'fullWidth' && modalSizeToClass[size],
        fullHeight && 'h-full',
        className,
      )}
      {...props}
      ref={ref}
    >
      {children}
    </DialogContent>
  ),
);

export const ModalFooter = ({
  className,
  children,
  ...props
}: React.ComponentProps<'footer'>) => (
  <footer
    className={tailwindMerge(
      'bg-background-white p-l tablet:flex-row tablet:rounded-b tablet:bg-background-grey-light tablet:p-m flex flex-col justify-between',
      className,
    )}
    {...props}
  >
    {children}
  </footer>
);

ModalFooter.displayName = 'Modal.Footer';

export const ModalBody = ({
  children,
  className,
  fullHeight = false,
  scrollable = true,
  ...props
}: React.ComponentProps<'div'> & {
  fullHeight?: boolean;
  scrollable?: boolean;
}) => (
  <Suspense
    fallback={
      <SpinnerAnimatedSolidIcon className="my-xl fill-primary self-center" />
    }
  >
    <div
      className={tailwindMerge(
        'min-h-zero p-l tablet:p-m flex flex-1 flex-col',
        !fullHeight && 'tablet:flex-[revert]',
        scrollable && 'overflow-auto',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  </Suspense>
);
